/**
 * @element qds-select
 *
 * Single and multiple choice from a known set (2 to ~200 options).
 * Uses a popover listbox pattern with optional filtering, grouping,
 * and tag display for multi-select.
 *
 * @attr {string} value - Selected value in single mode.
 * @attr {boolean} multiple - Enables multi-select mode. Reflected.
 * @attr {string} placeholder - Trigger text when empty (default "Select…").
 * @attr {boolean} filterable - true | false | auto (default auto: true above 10 options).
 * @attr {boolean} clearable - Shows a clear button when non-empty.
 * @attr {number} max-selected - Maximum selections in multiple mode.
 * @attr {number} tag-threshold - Above this count, trigger shows "N selected" (default 3).
 * @attr {string} placement - auto | bottom | top (default auto).
 * @attr {boolean} open - Reflected; settable to open/close programmatically.
 * @attr {string} name - Submitted form key.
 * @attr {string} label - Rendered as a real <label for> in shadow root.
 * @attr {string} hint - Helper text below the trigger.
 * @attr {string} error-text - Overrides built-in validation message.
 * @attr {string} size - sm | md | lg (default md).
 * @attr {boolean} disabled - Not focusable, not submitted. Reflected.
 * @attr {boolean} readonly - Focusable, submitted, no popup. Reflected.
 * @attr {boolean} required - Validation flag. Reflected.
 * @attr {boolean} invalid - Author-forced invalid state. Reflected.
 * @attr {string} validate-on - blur | input | submit (default blur).
 *
 * @property {QdsOption[]} options - Property only; arrays are not attributes.
 * @property {string[]} values - Selected values in multiple mode; property only.
 * @property {ValidityState | QdsValidity} validity - Read-only.
 *
 * @csspart container - Outer wrapper.
 * @csspart label - The <label> element.
 * @csspart trigger - The combobox trigger surface.
 * @csspart trigger-text - Text or tags inside the trigger.
 * @csspart tag - Individual selection tag (multiple mode).
 * @csspart tag-remove - Remove button on a tag.
 * @csspart chevron - Dropdown arrow icon.
 * @csspart clear-button - Clear-all button.
 * @csspart listbox - The popup listbox panel.
 * @csspart filter - The filter input wrapper.
 * @csspart group-label - Optgroup heading.
 * @csspart option - Individual option row.
 * @csspart option-check - Checkmark icon on selected option.
 * @csspart option-description - Description text on an option.
 * @csspart empty - Empty-state message.
 * @csspart hint - Helper text.
 * @csspart error - Error message.
 *
 * @fires change - Selection committed. Bubbles, composed.
 * @fires input - Same as change; native parity. Bubbles, composed.
 * @fires qds-open - Listbox opened. detail: { component }.
 * @fires qds-close - Listbox closed. detail: { component }.
 * @fires qds-filter - Filter text changed. detail: { component, query }.
 * @fires qds-validity-change - Validity transition. detail: QdsValidityChangeEventDetail.
 *
 * @example
 * <qds-select label="Country" placeholder="Choose…">
 *   <option value="us">United States</option>
 *   <option value="uk">United Kingdom</option>
 * </qds-select>
 */

import {
  attachInternalsSafe,
  ValidityTracker,
  dispatchValidityChange,
  generateControlId,
  isAttrTruthy,
  reflectBoolean,
  parseSize,
  parseValidateOn,
  getFormControlStyles,
  upgradeProperty,
} from '../shared/form-control.js';
import { PopoverController } from '../shared/popover-utils.js';
import { loadBaseCSS, adoptPrimeIconsStyles } from '../shared/button-utils.js';
import type { QdsOption, QdsControlSize, QdsValidateOn, QdsValidity } from '../../types/index';

export class QdsSelect extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;

  static get observedAttributes(): string[] {
    return [
      'value', 'multiple', 'placeholder', 'filterable', 'clearable',
      'max-selected', 'tag-threshold', 'placement', 'open',
      'name', 'label', 'hint', 'error-text', 'size', 'disabled',
      'readonly', 'required', 'invalid', 'validate-on',
    ];
  }

  // ── Private fields ──────────────────────────────────────────────
  private _internals?: ElementInternals;
  private _validityTracker: ValidityTracker;

  private _options: QdsOption[] = [];
  private _slottedOptions: QdsOption[] = [];
  private _value: string = '';
  private _values: string[] = [];
  private _multiple: boolean = false;
  private _placeholder: string = 'Select…';
  private _filterable: boolean | null = null; // null = auto
  private _clearable: boolean = false;
  private _maxSelected: number | null = null;
  private _tagThreshold: number = 3;
  private _placement: 'auto' | 'bottom' | 'top' = 'auto';
  private _open: boolean = false;
  private _name: string = '';
  private _label: string = '';
  private _hint: string = '';
  private _errorText: string = '';
  private _size: QdsControlSize = 'md';
  private _disabled: boolean = false;
  private _formDisabled = false;
  private _readonly: boolean = false;
  private _required: boolean = false;
  private _invalid: boolean = false;
  private _validateOn: QdsValidateOn = 'blur';
  private _customValidityMsg: string = '';

  private _filterQuery: string = '';
  private _activeIndex: number = -1;
  private _hasShownError: boolean = false;
  private _initialValue: string = '';
  private _initialValues: string[] = [];

  private _popoverController: PopoverController | null = null;
  private _cleanupFns: Array<() => void> = [];
  private _isRendering = false;
  private _listboxPointerActive = false;
  private _listboxBlurPending = false;

  // ── IDs for ARIA ────────────────────────────────────────────────
  private _id: string;
  private _labelId: string;
  private _hintId: string;
  private _errorId: string;
  private _listboxId: string;
  private _triggerId: string;
  private _filterId: string;

  // ── Constructor ─────────────────────────────────────────────────
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    // jsdom 26 may return an ElementInternals-like object without setFormValue/validity.
    // Only keep internals when the API is genuinely available.
    const internals = attachInternalsSafe(this);
    this._internals = (internals && typeof (internals as any).setFormValue === 'function')
      ? internals
      : undefined;
    this._validityTracker = new ValidityTracker(this._internals);

    this._id = generateControlId('select');
    this._labelId = `${this._id}-label`;
    this._hintId = `${this._id}-hint`;
    this._errorId = `${this._id}-error`;
    this._listboxId = `${this._id}-listbox`;
    this._triggerId = `${this._id}-trigger`;
    this._filterId = `${this._id}-filter`;

    loadBaseCSS();
  }

  // ── Safe form value setter ──────────────────────────────────────
  /** Guards setFormValue — jsdom 26 may return an ElementInternals-like object without the method. */
  private _setFormValue(value: string): void {
    if (this._internals && typeof (this._internals as any).setFormValue === 'function') {
      try {
        (this._internals as any).setFormValue(value);
      } catch {
        /* noop */
      }
    }
  }

  // ── Lifecycle ───────────────────────────────────────────────────
  connectedCallback(): void {
    if (!this.isConnected) return;
    upgradeProperty(this, 'options');
    upgradeProperty(this, 'values');
    this._initialValue = this._value;
    this._initialValues = [...this._values];
    this._readSlottedOptions();
    this._updateValidity();
    this._setFormValue(this._multiple ? this._values.join(',') : this._value);
    this.render();
  }

  disconnectedCallback(): void {
    // Set _open to false first so the wrapped hide() in destroy() doesn't
    // call _onPopoverDismissed() and re-render a detached element.
    this._open = false;
    reflectBoolean(this, 'open', false);
    if (this._popoverController) {
      this._popoverController.destroy();
      this._popoverController = null;
    }
    this._cleanupListeners();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'value':
        if (!this._multiple) {
          this._value = newValue || '';
          if (this.isConnected) {
            this._setFormValue(this._value);
            this._updateValidity();
          }
        }
        break;
      case 'multiple':
        this._multiple = isAttrTruthy(newValue);
        break;
      case 'placeholder':
        this._placeholder = newValue || 'Select…';
        break;
      case 'filterable':
        this._filterable = newValue === null ? null : newValue === 'false' ? false : true;
        break;
      case 'clearable':
        this._clearable = isAttrTruthy(newValue);
        break;
      case 'max-selected':
        this._maxSelected = newValue ? parseInt(newValue, 10) : null;
        break;
      case 'tag-threshold':
        this._tagThreshold = newValue ? parseInt(newValue, 10) : 3;
        break;
      case 'placement':
        this._placement = (newValue === 'bottom' || newValue === 'top') ? newValue : 'auto';
        break;
      case 'open': {
        const newOpen = isAttrTruthy(newValue);
        if (this._open !== newOpen) {
          if (newOpen) this._openListbox();
          else this._closeListbox();
        }
        return;
      }
      case 'name':
        this._name = newValue || '';
        break;
      case 'label':
        this._label = newValue || '';
        break;
      case 'hint':
        this._hint = newValue || '';
        break;
      case 'error-text':
        this._errorText = newValue || '';
        break;
      case 'size':
        this._size = parseSize(newValue);
        break;
      case 'disabled':
        this._disabled = this._formDisabled || isAttrTruthy(newValue);
        if (this._open) this._closeListbox();
        break;
      case 'readonly':
        this._readonly = isAttrTruthy(newValue);
        if (this._open) this._closeListbox();
        break;
      case 'required':
        this._required = isAttrTruthy(newValue);
        if (this.isConnected) this._updateValidity();
        break;
      case 'invalid':
        this._invalid = isAttrTruthy(newValue);
        break;
      case 'validate-on':
        this._validateOn = parseValidateOn(newValue);
        break;
    }

    if (this.isConnected) {
      this.render();
    }
  }

  // ── Public properties ───────────────────────────────────────────

  get options(): QdsOption[] {
    return [...this._options];
  }
  set options(opts: QdsOption[]) {
    this._options = opts ? [...opts] : [];
    if (this.isConnected) {
      this._updateValidity();
      this.render();
    }
  }

  get value(): string {
    return this._value;
  }
  set value(v: string) {
    this._value = v;
    if (this.isConnected) {
      this._setFormValue(this._multiple ? this._values.join(',') : this._value);
      this._updateValidity();
      this.render();
    }
  }

  get values(): string[] {
    return [...this._values];
  }
  set values(v: string[]) {
    this._values = v ? [...v] : [];
    if (this.isConnected) {
      this._setFormValue(this._values.join(','));
      this._updateValidity();
      this.render();
    }
  }

  get multiple(): boolean { return this._multiple; }
  set multiple(v: boolean) { reflectBoolean(this, 'multiple', v); }

  get placeholder(): string { return this._placeholder; }
  set placeholder(v: string) {
    this._placeholder = v;
    if (v) this.setAttribute('placeholder', v);
    else this.removeAttribute('placeholder');
  }

  get filterable(): boolean | 'auto' {
    return this._filterable === null ? 'auto' : this._filterable;
  }
  set filterable(v: boolean | 'auto') {
    if (v === 'auto') this.removeAttribute('filterable');
    else if (v) this.setAttribute('filterable', '');
    else this.setAttribute('filterable', 'false');
  }

  get clearable(): boolean { return this._clearable; }
  set clearable(v: boolean) { reflectBoolean(this, 'clearable', v); }

  get maxSelected(): number | null { return this._maxSelected; }
  set maxSelected(v: number | null) {
    if (v !== null) this.setAttribute('max-selected', String(v));
    else this.removeAttribute('max-selected');
  }

  get tagThreshold(): number { return this._tagThreshold; }
  set tagThreshold(v: number) { this.setAttribute('tag-threshold', String(v)); }

  get placement(): 'auto' | 'bottom' | 'top' { return this._placement; }
  set placement(v: 'auto' | 'bottom' | 'top') { this.setAttribute('placement', v); }

  get open(): boolean { return this._open; }
  set open(v: boolean) {
    if (this._open !== v) {
      if (v) this._openListbox();
      else this._closeListbox();
    }
  }

  get name(): string { return this._name; }
  set name(v: string) {
    if (v) this.setAttribute('name', v);
    else this.removeAttribute('name');
  }

  get label(): string { return this._label; }
  set label(v: string) {
    if (v) this.setAttribute('label', v);
    else this.removeAttribute('label');
  }

  get hint(): string { return this._hint; }
  set hint(v: string) {
    if (v) this.setAttribute('hint', v);
    else this.removeAttribute('hint');
  }

  get errorText(): string { return this._errorText; }
  set errorText(v: string) {
    if (v) this.setAttribute('error-text', v);
    else this.removeAttribute('error-text');
  }

  get size(): QdsControlSize { return this._size; }
  set size(v: QdsControlSize) { this.setAttribute('size', v); }

  get disabled(): boolean { return this._disabled; }
  set disabled(v: boolean) { reflectBoolean(this, 'disabled', v); }

  get readonly(): boolean { return this._readonly; }
  set readonly(v: boolean) { reflectBoolean(this, 'readonly', v); }

  get required(): boolean { return this._required; }
  set required(v: boolean) { reflectBoolean(this, 'required', v); }

  get invalid(): boolean { return this._invalid; }
  set invalid(v: boolean) { reflectBoolean(this, 'invalid', v); }

  get validateOn(): QdsValidateOn { return this._validateOn; }
  set validateOn(v: QdsValidateOn) { this.setAttribute('validate-on', v); }

  // ── Form association ────────────────────────────────────────────

  get validity(): QdsValidity {
    return this._validityTracker.validity;
  }

  checkValidity(): boolean {
    this._updateValidity();
    return this._validityTracker.checkValidity();
  }

  reportValidity(): boolean {
    this._updateValidity();
    this._hasShownError = true;
    const valid = this._validityTracker.reportValidity();
    if (!valid) {
      this.setAttribute('data-invalid', '');
    } else {
      this.removeAttribute('data-invalid');
    }
    dispatchValidityChange(this, this._validityTracker);
    this.render();
    return valid;
  }

  setCustomValidity(msg: string): void {
    this._customValidityMsg = msg;
    this._validityTracker.setCustomValidity(msg);
    if (msg) {
      this.setAttribute('data-invalid', '');
    } else {
      this.removeAttribute('data-invalid');
    }
    if (this.isConnected) {
      this.render();
    }
  }

  formResetCallback(): void {
    this._value = this._initialValue;
    this._values = [...this._initialValues];
    this._hasShownError = false;
    this._customValidityMsg = '';
    this._validityTracker.setValidity({ valueMissing: false, valid: true }, '');
    this.removeAttribute('data-invalid');
    this._setFormValue(this._multiple ? this._values.join(',') : this._value);
    this.render();
  }

  formDisabledCallback(disabled: boolean): void {
    this._formDisabled = disabled;
    this._disabled = disabled || isAttrTruthy(this.getAttribute('disabled'));
    if (this.isConnected) {
      this.render();
    }
  }

  // ── Rendering ───────────────────────────────────────────────────

  private render(): void {
    if (!this.shadowRoot) return;
    if (this._isRendering) return;
    this._isRendering = true;

    // Tear down
    if (this._popoverController) {
      this._popoverController.destroy();
      this._popoverController = null;
    }
    this._cleanupListeners();

    const showListbox = this._open && !this._disabled && !this._readonly;

    this.shadowRoot.innerHTML = `
      <style>
        ${getFormControlStyles()}
        ${this._getSelectStyles()}
      </style>
      <div part="container" class="qds-select-container">
        ${this._renderLabel()}
        <div class="qds-select-wrapper">
          ${this._renderTrigger(showListbox)}
          ${showListbox ? this._renderListbox() : ''}
        </div>
        ${this._renderHint()}
        ${this._renderError()}
        <div class="qds-sr-only" aria-live="polite" id="${this._id}-live">${this._optionCountText(showListbox)}</div>
      </div>
      <slot style="display:none"></slot>
    `;

    void adoptPrimeIconsStyles(this.shadowRoot);
    this._wireEventListeners();

    if (showListbox) {
      this._setupPopover();
    }

    this._isRendering = false;
  }

  private _renderLabel(): string {
    if (!this._label) return '';
    const marker = this._required
      ? '<span class="qds-required-marker" aria-hidden="true">*</span>'
      : '';
    return `<label part="label" class="qds-label" id="${this._labelId}" for="${this._triggerId}">${this._escapeHtml(this._label)}${marker}</label>`;
  }

  private _renderTrigger(showListbox: boolean): string {
    const describedBy: string[] = [];
    if (this._hint) describedBy.push(this._hintId);
    if (this._isErrorVisible()) describedBy.push(this._errorId);

    const ariaLabelledBy = this._label ? `aria-labelledby="${this._labelId}"` : '';
    const ariaDescribedBy = describedBy.length > 0 ? `aria-describedby="${describedBy.join(' ')}"` : '';
    const ariaInvalid = this._isInvalid() ? 'aria-invalid="true"' : '';
    const ariaRequired = this._required ? 'aria-required="true"' : '';
    const tabindex = this._disabled ? -1 : 0;
    const openClass = showListbox ? 'qds-open' : '';

    return `
      <div part="trigger"
           class="qds-control-surface qds-select-trigger ${openClass}"
           role="combobox"
           aria-disabled="${this._disabled}"
           aria-readonly="${this._readonly}"
           aria-expanded="${showListbox}"
           aria-haspopup="listbox"
           aria-controls="${this._listboxId}"
           ${ariaLabelledBy}
           ${ariaDescribedBy}
           ${ariaInvalid}
           ${ariaRequired}
           tabindex="${tabindex}"
           id="${this._triggerId}">
        ${this._renderTriggerInner()}
      </div>
    `;
  }

  private _renderTriggerInner(): string {
    let content: string;

    if (this._multiple) {
      if (this._values.length === 0) {
        content = `<span part="trigger-text" class="qds-trigger-text qds-placeholder">${this._escapeHtml(this._placeholder)}</span>`;
      } else if (this._values.length > this._tagThreshold) {
        content = `<span part="trigger-text" class="qds-trigger-text">${this._values.length} selected</span>`;
      } else {
        const tags = this._values.map(v => {
          const opt = this._allOptions.find(o => o.value === v);
          const label = opt ? opt.label : v;
          return `<span part="tag" class="qds-tag">${this._escapeHtml(label)}<button part="tag-remove" class="qds-tag-remove" data-value="${this._escapeHtml(v)}" aria-label="Remove ${this._escapeHtml(label)}" tabindex="-1">×</button></span>`;
        }).join('');
        content = `<span part="trigger-text" class="qds-trigger-text qds-tags-container">${tags}</span>`;
      }
    } else {
      if (this._value) {
        const opt = this._allOptions.find(o => o.value === this._value);
        const label = opt ? opt.label : this._value;
        content = `<span part="trigger-text" class="qds-trigger-text">${this._escapeHtml(label)}</span>`;
      } else {
        content = `<span part="trigger-text" class="qds-trigger-text qds-placeholder">${this._escapeHtml(this._placeholder)}</span>`;
      }
    }

    const clearBtn = this._clearable && this._hasValue()
      ? `<button part="clear-button" class="qds-clear-button" data-action="clear" aria-label="Clear${this._label ? ' ' + this._escapeHtml(this._label) : ''}" tabindex="-1">×</button>`
      : '';

    const chevron = `<i part="chevron" class="pi pi-chevron-down qds-chevron"></i>`;

    return `${content}${clearBtn}${chevron}`;
  }

  private _renderListbox(): string {
    const filterable = this._isFilterable();
    const opts = this._effectiveOptions;
    const ariaMulti = this._multiple ? 'aria-multiselectable="true"' : '';

    const filterHtml = filterable ? `
      <div class="qds-filter-wrapper" part="filter">
        <input type="text"
               class="qds-filter-input"
               role="searchbox"
               aria-label="Filter options"
               aria-controls="${this._listboxId}"
               id="${this._filterId}"
               value="${this._escapeHtml(this._filterQuery)}"
               placeholder="Filter…" />
      </div>
    ` : '';

    const optionsHtml = opts.length > 0
      ? this._renderOptionsHtml()
      : `<div part="empty" class="qds-empty">No results found</div>`;

    return `
      <div class="qds-listbox-wrapper"
           part="listbox"
           role="listbox"
           id="${this._listboxId}"
           ${ariaMulti}
           aria-labelledby="${this._labelId}">
        ${filterHtml}
        <div class="qds-options-container">
          ${optionsHtml}
        </div>
      </div>
    `;
  }

  private _renderOptionsHtml(): string {
    const opts = this._effectiveOptions;
    let html = '';
    const seenGroups = new Set<string>();
    let groupIdx = 0;

    for (let i = 0; i < opts.length; i++) {
      const opt = opts[i];
      const g = opt.group;
      if (g && !seenGroups.has(g)) {
        const groupId = `${this._id}-group-${groupIdx++}`;
        html += `<div part="group-label" class="qds-group-label" id="${groupId}">${this._escapeHtml(g)}</div>`;
        seenGroups.add(g);
      }
      html += this._renderOption(opt, i);
    }
    return html;
  }

  private _renderOption(opt: QdsOption, index: number): string {
    const selected = this._isOptionSelected(opt);
    const isActive = index === this._activeIndex;
    const disabledCls = opt.disabled ? 'qds-option-disabled' : '';
    const optId = `${this._id}-opt-${index}`;

    return `
      <div part="option"
           class="qds-option ${selected ? 'qds-option-selected' : ''} ${isActive ? 'qds-option-active' : ''} ${disabledCls}"
           role="option"
           id="${optId}"
           data-index="${index}"
           aria-selected="${selected}"
           ${opt.disabled ? 'aria-disabled="true"' : ''}>
        <span part="option-check" class="qds-option-check">${selected ? '<i class="pi pi-check"></i>' : ''}</span>
        ${opt.icon ? `<i class="pi ${this._escapeHtml(opt.icon)} qds-option-icon"></i>` : ''}
        <span class="qds-option-label">${this._escapeHtml(opt.label)}</span>
        ${opt.description ? `<span part="option-description" class="qds-option-description">${this._escapeHtml(opt.description)}</span>` : ''}
      </div>
    `;
  }

  private _renderHint(): string {
    if (!this._hint) return '';
    return `<div part="hint" class="qds-hint" id="${this._hintId}">${this._escapeHtml(this._hint)}</div>`;
  }

  private _renderError(): string {
    const errText = this._getErrorText();
    if (!errText) return '';
    return `<div part="error" class="qds-error" id="${this._errorId}" role="alert">${this._escapeHtml(errText)}</div>`;
  }

  private _optionCountText(showListbox: boolean): string {
    if (!showListbox) return '';
    const count = this._effectiveOptions.length;
    if (count === 0) return 'No results found';
    return `${count} option${count !== 1 ? 's' : ''} available`;
  }

  private _getSelectStyles(): string {
    return `
      .qds-select-container {
        display: inline-flex;
        flex-direction: column;
        width: 100%;
      }

      .qds-select-wrapper {
        position: relative;
        display: block;
        min-width: 180px;
      }

      .qds-select-trigger {
        cursor: pointer;
        width: 100%;
        gap: var(--theme-space-2, 8px);
        user-select: none;
      }
      .qds-select-trigger.qds-open .qds-chevron {
        transform: rotate(180deg);
      }

      .qds-trigger-text {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .qds-trigger-text.qds-placeholder {
        color: var(--theme-text-tertiary, #6A6A6A);
      }
      .qds-tags-container {
        display: flex;
        flex-wrap: wrap;
        gap: 2px;
        overflow: visible;
        text-overflow: clip;
      }

      .qds-chevron {
        font-size: var(--icon-size, 20px);
        color: var(--icon-color-secondary, var(--theme-text-tertiary, #6A6A6A));
        flex-shrink: 0;
        transition: transform var(--transition-speed-fast, 0.15s);
      }

      .qds-clear-button {
        border: none;
        background: transparent;
        cursor: pointer;
        color: var(--theme-text-tertiary, #6A6A6A);
        font-size: 16px;
        padding: 0 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 24px;
        min-height: 24px;
        border-radius: var(--theme-radius-sm, 4px);
        flex-shrink: 0;
      }
      .qds-clear-button:hover {
        color: var(--theme-text-primary, #333);
        background: var(--button-hover-bg, rgba(0,0,0,0.04));
      }

      .qds-tag {
        display: inline-flex;
        align-items: center;
        gap: var(--theme-space-1, 4px);
        background: var(--theme-background-tertiary, #F0F1F5);
        border-radius: var(--theme-radius-sm, 4px);
        padding: 2px var(--theme-space-1, 4px);
        font-size: 13px;
        margin: 1px 0;
      }
      .qds-tag-remove {
        border: none;
        background: transparent;
        cursor: pointer;
        color: var(--theme-text-tertiary, #6A6A6A);
        font-size: 14px;
        padding: 0;
        line-height: 1;
      }
      .qds-tag-remove:hover {
        color: var(--theme-text-primary, #333);
      }

      .qds-listbox-wrapper {
        position: absolute;
        left: 0;
        right: 0;
        background: var(--theme-background-overlay, rgba(255,255,255,0.95));
        border: 1px solid var(--theme-border-primary, #E4E9F2);
        border-radius: var(--theme-radius-md, 8px);
        box-shadow: var(--theme-shadow-md, 0 8px 24px var(--theme-shadow-light, rgba(0,0,0,0.1)));
        max-height: 320px;
        overflow-y: auto;
        min-width: 100%;
        z-index: var(--qds-select-z, 1000);
        box-sizing: border-box;
      }

      .qds-filter-wrapper {
        padding: var(--theme-space-2, 8px);
        border-bottom: 1px solid var(--theme-border-primary, #E4E9F2);
        position: sticky;
        top: 0;
        background: var(--theme-background-overlay, rgba(255,255,255,0.95));
        z-index: 1;
      }
      .qds-filter-input {
        width: 100%;
        border: 1px solid var(--theme-border-primary, #E4E9F2);
        border-radius: var(--theme-radius-sm, 4px);
        padding: var(--theme-space-1, 4px) var(--theme-space-2, 8px);
        font-size: 14px;
        font-family: inherit;
        background: var(--theme-background-primary, #fff);
        color: var(--theme-text-primary, #333);
        box-sizing: border-box;
      }
      .qds-filter-input:focus {
        outline: none;
        border-color: var(--theme-accent-primary, #445E89);
        box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
      }

      .qds-group-label {
        padding: var(--theme-space-1, 4px) var(--theme-space-3, 12px);
        font-size: 12px;
        font-weight: 600;
        color: var(--theme-text-tertiary, #6A6A6A);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-top: var(--theme-space-1, 4px);
      }

      .qds-option {
        display: flex;
        align-items: center;
        height: var(--qds-control-height, 40px);
        padding: 0 var(--theme-space-3, 12px);
        cursor: pointer;
        gap: var(--theme-space-2, 8px);
        font-size: 14px;
        color: var(--theme-text-primary, #333);
        box-sizing: border-box;
      }
      .qds-option:hover,
      .qds-option.qds-option-active {
        background: var(--button-hover-bg, rgba(0,0,0,0.04));
      }
      .qds-option.qds-option-selected {
        background: var(--theme-background-accent-subtle, rgba(68, 94, 137, 0.08));
      }
      .qds-option.qds-option-selected.qds-option-active {
        background: var(--theme-background-accent-subtle, rgba(68, 94, 137, 0.12));
      }
      .qds-option.qds-option-disabled {
        opacity: 0.55;
        cursor: not-allowed;
        pointer-events: none;
      }

      .qds-option-check {
        color: var(--theme-accent-primary, #445E89);
        flex-shrink: 0;
        width: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
      }
      .qds-option-icon {
        font-size: var(--icon-size, 20px);
        color: var(--icon-color-secondary, var(--theme-text-tertiary, #6A6A6A));
        flex-shrink: 0;
      }
      .qds-option-label {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .qds-option-description {
        font-size: 12px;
        color: var(--theme-text-tertiary, #6A6A6A);
        white-space: nowrap;
        flex-shrink: 0;
      }

      .qds-empty {
        padding: var(--theme-space-4, 16px);
        text-align: center;
        color: var(--theme-text-tertiary, #6A6A6A);
        font-size: 14px;
      }

      .qds-sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
    `;
  }

  // ── Event wiring ────────────────────────────────────────────────

  private _on(target: EventTarget, event: string, handler: EventListener, options?: AddEventListenerOptions): void {
    target.addEventListener(event, handler, options);
    this._cleanupFns.push(() => target.removeEventListener(event, handler, options));
  }

  private _cleanupListeners(): void {
    this._cleanupFns.forEach(fn => fn());
    this._cleanupFns = [];
  }

  private _wireEventListeners(): void {
    const sr = this.shadowRoot;
    if (!sr) return;

    const listbox = sr.querySelector('[part="listbox"]');
    if (listbox) {
      let pointerResetTimer: ReturnType<typeof setTimeout> | undefined;
      this._on(listbox, 'pointerdown', () => {
        // Scrollbars can blur the trigger/filter with no relatedTarget. Capture
        // the origin before that blur without canceling native scrollbar dragging.
        this._listboxPointerActive = true;
        clearTimeout(pointerResetTimer);
        pointerResetTimer = setTimeout(() => {
          this._listboxPointerActive = false;
        }, 0);
      }, { capture: true });

      // After a scrollbar blur there may be no focused control to emit another
      // focusout. Finish blur validation when the user next interacts outside.
      const onOutsideInteraction = (e: Event): void => {
        if (!this._listboxBlurPending) return;
        if (e.composedPath().includes(this)) {
          if (e.type === 'focusin') this._listboxBlurPending = false;
          return;
        }
        this._handleFocusLeave();
      };
      this._on(document, 'pointerdown', onOutsideInteraction, { capture: true });
      this._on(document, 'focusin', onOutsideInteraction, { capture: true });
      // Internal shadow focus moves are not always composed to document.
      this._on(sr, 'focusin', () => { this._listboxBlurPending = false; });
      this._cleanupFns.push(() => {
        clearTimeout(pointerResetTimer);
        this._listboxPointerActive = false;
        this._listboxBlurPending = false;
      });
    }

    const trigger = sr.querySelector('[part="trigger"]') as HTMLElement | null;
    if (trigger) {
      const onTriggerClick = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        if (this._disabled || this._readonly) return;
        if (this._open) this._closeListbox();
        else this._openListbox();
      };
      this._on(trigger, 'click', onTriggerClick as EventListener);

      const onTriggerKeyDown = (e: KeyboardEvent): void => {
        if (this._disabled || this._readonly) return;
        if (this._open && this._isFilterable()) return; // filter input handles keys when open+filterable
        this._handleKeyDown(e, false);
      };
      this._on(trigger, 'keydown', onTriggerKeyDown as EventListener);

      const onFocusOut = (e: FocusEvent): void => {
        this._handleFocusOut(e);
      };
      this._on(trigger, 'focusout', onFocusOut as EventListener);
    }

    const filterInput = sr.querySelector('.qds-filter-input') as HTMLInputElement | null;
    if (filterInput) {
      const onFilterInput = (e: Event): void => {
        const input = e.target as HTMLInputElement;
        this._filterQuery = input.value;
        this._updateFilter();
      };
      this._on(filterInput, 'input', onFilterInput as EventListener);

      const onFilterKeyDown = (e: KeyboardEvent): void => {
        this._handleKeyDown(e, true);
      };
      this._on(filterInput, 'keydown', onFilterKeyDown as EventListener);

      const onFilterFocusOut = (e: FocusEvent): void => {
        this._handleFocusOut(e);
      };
      this._on(filterInput, 'focusout', onFilterFocusOut as EventListener);
    }

    // Option click handlers
    const optionEls = sr.querySelectorAll('[role="option"]');
    optionEls.forEach(optEl => {
      // Options are not focusable (no tabindex). Without this, a real mouse
      // mousedown on an option blurs the trigger — focus has nowhere to
      // land inside the shadow root — and the trigger's focusout handler
      // (correctly, by its own rules) reads that as "focus left the
      // component" and closes the listbox via a re-render before the click
      // event this same mousedown started ever reaches the option's own
      // click handler. preventDefault on mousedown blocks that browser
      // default focus-shift so the click completes normally.
      const onOptionMouseDown = (e: MouseEvent): void => {
        e.preventDefault();
      };
      this._on(optEl, 'mousedown', onOptionMouseDown as EventListener);

      const onOptionClick = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        const option = e.currentTarget as HTMLElement;
        const index = parseInt(option.getAttribute('data-index') || '-1', 10);
        if (index >= 0) {
          this._selectOption(index);
        }
      };
      this._on(optEl, 'click', onOptionClick as EventListener);
    });

    // Clear button
    const clearBtn = sr.querySelector('[data-action="clear"]');
    if (clearBtn) {
      const onClearClick = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        if (this._multiple) {
          this._values = [];
        } else {
          this._value = '';
        }
        this._commitValue();
        if (this._open) {
          this._updateTriggerInPlace();
          this._updateOptionsStateInPlace();
        } else {
          this.render();
        }
      };
      this._on(clearBtn, 'click', onClearClick as EventListener);
    }

    // Tag remove buttons
    const tagRemoves = sr.querySelectorAll('[part="tag-remove"]');
    tagRemoves.forEach(btn => {
      const onTagRemove = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const value = target.getAttribute('data-value');
        if (value) {
          this._values = this._values.filter(v => v !== value);
          this._commitValue();
          if (this._open) {
            this._updateTriggerInPlace();
            this._updateOptionsStateInPlace();
          } else {
            this.render();
          }
        }
      };
      this._on(btn, 'click', onTagRemove as EventListener);
    });

    // Slot change for slotted <option> children
    const slot = sr.querySelector('slot');
    if (slot) {
      const onSlotChange = (): void => {
        const prev = JSON.stringify(this._slottedOptions);
        this._readSlottedOptions();
        if (JSON.stringify(this._slottedOptions) === prev) return;
        this._updateValidity();
        this.render();
      };
      this._on(slot, 'slotchange', onSlotChange as EventListener);
    }
  }

  private _wireTriggerInnerListeners(trigger: HTMLElement): void {
    // Clear button
    const clearBtn = trigger.querySelector('[data-action="clear"]');
    if (clearBtn) {
      const onClearClick = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        if (this._multiple) {
          this._values = [];
        } else {
          this._value = '';
        }
        this._commitValue();
        if (this._open) {
          this._updateTriggerInPlace();
          this._updateOptionsStateInPlace();
        } else {
          this.render();
        }
      };
      this._on(clearBtn, 'click', onClearClick as EventListener);
    }

    // Tag remove buttons
    const tagRemoves = trigger.querySelectorAll('[part="tag-remove"]');
    tagRemoves.forEach(btn => {
      const onTagRemove = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const value = target.getAttribute('data-value');
        if (value) {
          this._values = this._values.filter(v => v !== value);
          this._commitValue();
          if (this._open) {
            this._updateTriggerInPlace();
            this._updateOptionsStateInPlace();
          } else {
            this.render();
          }
        }
      };
      this._on(btn, 'click', onTagRemove as EventListener);
    });
  }

  // ── Popover management ──────────────────────────────────────────

  private _setupPopover(): void {
    const sr = this.shadowRoot;
    if (!sr) return;

    const trigger = sr.querySelector('[part="trigger"]') as HTMLElement | null;
    const panel = sr.querySelector('[part="listbox"]') as HTMLElement | null;
    if (!trigger || !panel) return;

    const ctrl = new PopoverController(this, trigger, panel);

    // Wrap hide to detect external dismissal (outside click, Escape, other instance)
    const origHide = ctrl.hide.bind(ctrl);
    ctrl.hide = (): void => {
      origHide();
      if (this._open) {
        this._onPopoverDismissed();
      }
    };

    ctrl.show();

    // Override placement when forced
    if (this._placement !== 'auto') {
      if (this._placement === 'top') {
        panel.style.bottom = `${trigger.offsetHeight}px`;
        panel.style.top = '';
      } else {
        panel.style.top = `${trigger.offsetHeight}px`;
        panel.style.bottom = '';
      }
    }

    this._popoverController = ctrl;

    // Focus filter input if filterable, otherwise trigger
    if (this._isFilterable()) {
      const filterInput = sr.querySelector('.qds-filter-input') as HTMLElement | null;
      if (filterInput) filterInput.focus();
    } else {
      trigger.focus();
    }

    this._updateActiveDescendant();
  }

  private _openListbox(openAtEnd: boolean = false): void {
    if (this._disabled || this._readonly) return;
    this._open = true;
    reflectBoolean(this, 'open', true);
    this._filterQuery = '';
    this._activeIndex = openAtEnd
      ? this._findLastEnabledIndex()
      : this._findFirstEnabledIndex();
    this.render();
    // _setupPopover is called by render() when showListbox is true — no duplicate call here.

    this.dispatchEvent(new CustomEvent('qds-open', {
      bubbles: true,
      composed: true,
      detail: { component: this },
    }));
  }

  private _closeListbox(): void {
    if (!this._open) return;
    this._open = false;
    reflectBoolean(this, 'open', false);
    this._filterQuery = '';

    const ctrl = this._popoverController;
    this._popoverController = null;
    if (ctrl) {
      ctrl.destroy();
    }

    this.render();

    this.dispatchEvent(new CustomEvent('qds-close', {
      bubbles: true,
      composed: true,
      detail: { component: this },
    }));
  }

  private _onPopoverDismissed(): void {
    this._open = false;
    reflectBoolean(this, 'open', false);
    this._filterQuery = '';
    this._popoverController = null;
    this.render();

    // Restore focus to trigger
    const trigger = this.shadowRoot?.querySelector('[part="trigger"]') as HTMLElement | null;
    if (trigger && this.isConnected) {
      trigger.focus();
    }

    this.dispatchEvent(new CustomEvent('qds-close', {
      bubbles: true,
      composed: true,
      detail: { component: this },
    }));
  }

  // ── Selection ───────────────────────────────────────────────────

  private _selectOption(index: number): void {
    const opts = this._effectiveOptions;
    const opt = opts[index];
    if (!opt || opt.disabled) return;

    if (this._multiple) {
      const idx = this._values.indexOf(opt.value);
      if (idx >= 0) {
        // Deselect
        this._values = this._values.filter(v => v !== opt.value);
      } else {
        // Check max-selected
        if (this._maxSelected !== null && this._values.length >= this._maxSelected) {
          return; // Block selection
        }
        this._values = [...this._values, opt.value];
      }
      this._commitValue();
      // Stay open — update trigger and options in place
      this._updateTriggerInPlace();
      this._updateOptionsStateInPlace();
    } else {
      this._value = opt.value;
      this._commitValue();
      this._closeListbox();
    }
  }

  private _commitValue(): void {
    if (this._multiple) {
      this._setFormValue(this._values.join(','));
    } else {
      this._setFormValue(this._value);
    }
    this._updateValidity();

    // Validate if needed
    if (this._validateOn === 'input' || this._hasShownError) {
      this._showErrorState();
    }

    // Dispatch native parity events
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));

    // Dispatch validity change if state transitioned
    if (this._validateOn === 'input' || this._hasShownError) {
      dispatchValidityChange(this, this._validityTracker);
    }
  }

  private _isOptionSelected(opt: QdsOption): boolean {
    if (this._multiple) {
      return this._values.includes(opt.value);
    }
    return this._value === opt.value;
  }

  private _hasValue(): boolean {
    return this._multiple ? this._values.length > 0 : !!this._value;
  }

  // ── Filter ──────────────────────────────────────────────────────

  private _isFilterable(): boolean {
    if (this._filterable !== null) return this._filterable;
    return this._allOptions.length > 10;
  }

  private _updateFilter(): void {
    this._activeIndex = this._findFirstEnabledIndex();
    this._updateListboxOptions();
    this._updateActiveDescendant();
    this._updateLiveRegion();

    this.dispatchEvent(new CustomEvent('qds-filter', {
      bubbles: true,
      composed: true,
      detail: { component: this, query: this._filterQuery },
    }));
  }

  private _updateListboxOptions(): void {
    const container = this.shadowRoot?.querySelector('.qds-options-container');
    if (!container) return;
    const opts = this._effectiveOptions;
    container.innerHTML = opts.length > 0
      ? this._renderOptionsHtml()
      : `<div part="empty" class="qds-empty">No results found</div>`;

    // Wire option click handlers on new elements
    const optionEls = container.querySelectorAll('[role="option"]');
    optionEls.forEach(optEl => {
      // See the matching comment in _wireEventListeners: options are not
      // focusable, so without this a real mousedown blurs the trigger and
      // closes the listbox before the click ever lands.
      const onOptionMouseDown = (e: MouseEvent): void => {
        e.preventDefault();
      };
      this._on(optEl, 'mousedown', onOptionMouseDown as EventListener);

      const onOptionClick = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        const option = e.currentTarget as HTMLElement;
        const index = parseInt(option.getAttribute('data-index') || '-1', 10);
        if (index >= 0) {
          this._selectOption(index);
        }
      };
      this._on(optEl, 'click', onOptionClick as EventListener);
    });
  }

  private _updateLiveRegion(): void {
    const live = this.shadowRoot?.querySelector(`#${this._id}-live`);
    if (live) {
      const count = this._effectiveOptions.length;
      live.textContent = count === 0
        ? 'No results found'
        : `${count} option${count !== 1 ? 's' : ''} available`;
    }
  }

  // ── Keyboard ────────────────────────────────────────────────────

  private _handleKeyDown(e: KeyboardEvent, isFilter: boolean): void {
    if (this._disabled || this._readonly) return;

    if (!this._open) {
      // Closed state — trigger is focused
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          this._openListbox();
          break;
        case 'ArrowDown':
          e.preventDefault();
          this._openListbox(false);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this._openListbox(true);
          break;
        default:
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            this._openListbox(false);
            // Seed filter
            this._filterQuery = e.key;
            const filterInput = this.shadowRoot?.querySelector('.qds-filter-input') as HTMLInputElement | null;
            if (filterInput) {
              filterInput.value = e.key;
              filterInput.focus();
            }
            this._updateFilter();
          }
          break;
      }
      return;
    }

    // Open state
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this._activeIndex >= 0) {
          this._selectOption(this._activeIndex);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        this._activeIndex = this._findNextEnabledIndex(this._activeIndex);
        this._updateActiveDescendant();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._activeIndex = this._findPrevEnabledIndex(this._activeIndex);
        this._updateActiveDescendant();
        break;
      case 'Home':
        e.preventDefault();
        this._activeIndex = this._findFirstEnabledIndex();
        this._updateActiveDescendant();
        break;
      case 'End':
        e.preventDefault();
        this._activeIndex = this._findLastEnabledIndex();
        this._updateActiveDescendant();
        break;
      case 'PageDown':
        e.preventDefault();
        this._activeIndex = this._findPageDownIndex(this._activeIndex);
        this._updateActiveDescendant();
        break;
      case 'PageUp':
        e.preventDefault();
        this._activeIndex = this._findPageUpIndex(this._activeIndex);
        this._updateActiveDescendant();
        break;
      case 'Tab':
        this._closeListbox();
        // Let Tab propagate for standard focus movement
        break;
      case 'Escape':
        // Handled by PopoverController
        break;
      default:
        // Printable characters on non-filter trigger: seed filter
        if (!isFilter && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          if (this._isFilterable()) {
            const filterInput = this.shadowRoot?.querySelector('.qds-filter-input') as HTMLInputElement | null;
            if (filterInput) {
              filterInput.focus();
              // Let the character type into the filter
            }
          }
        }
        break;
    }
  }

  // ── Navigation helpers ──────────────────────────────────────────

  private _findFirstEnabledIndex(): number {
    const opts = this._effectiveOptions;
    for (let i = 0; i < opts.length; i++) {
      if (!opts[i].disabled) return i;
    }
    return -1;
  }

  private _findLastEnabledIndex(): number {
    const opts = this._effectiveOptions;
    for (let i = opts.length - 1; i >= 0; i--) {
      if (!opts[i].disabled) return i;
    }
    return -1;
  }

  private _findNextEnabledIndex(from: number): number {
    const opts = this._effectiveOptions;
    if (opts.length === 0) return -1;
    for (let i = from + 1; i < opts.length; i++) {
      if (!opts[i].disabled) return i;
    }
    // Wrap
    for (let i = 0; i < from; i++) {
      if (!opts[i].disabled) return i;
    }
    return from;
  }

  private _findPrevEnabledIndex(from: number): number {
    const opts = this._effectiveOptions;
    if (opts.length === 0) return -1;
    for (let i = from - 1; i >= 0; i--) {
      if (!opts[i].disabled) return i;
    }
    // Wrap
    for (let i = opts.length - 1; i > from; i--) {
      if (!opts[i].disabled) return i;
    }
    return from;
  }

  private _findPageDownIndex(from: number): number {
    const opts = this._effectiveOptions;
    if (opts.length === 0) return -1;
    let target = Math.min(from + 10, opts.length - 1);
    while (target > from && opts[target].disabled) target--;
    if (opts[target]?.disabled) return from;
    return target;
  }

  private _findPageUpIndex(from: number): number {
    const opts = this._effectiveOptions;
    if (opts.length === 0) return -1;
    let target = Math.max(from - 10, 0);
    while (target < from && opts[target].disabled) target++;
    if (opts[target]?.disabled) return from;
    return target;
  }

  private _updateActiveDescendant(): void {
    const sr = this.shadowRoot;
    if (!sr) return;

    const filterInput = sr.querySelector('.qds-filter-input') as HTMLElement | null;
    const trigger = sr.querySelector('[part="trigger"]') as HTMLElement | null;
    const focusedEl = (filterInput && this._open) ? filterInput : trigger;

    if (!focusedEl) return;

    if (this._activeIndex >= 0) {
      const optId = `${this._id}-opt-${this._activeIndex}`;
      focusedEl.setAttribute('aria-activedescendant', optId);
    } else {
      focusedEl.removeAttribute('aria-activedescendant');
    }

    // Update active class on options
    const optionEls = sr.querySelectorAll('[role="option"]');
    optionEls.forEach(el => {
      const idx = parseInt(el.getAttribute('data-index') || '-1', 10);
      el.classList.toggle('qds-option-active', idx === this._activeIndex);
    });
  }

  // ── In-place DOM updates (no full re-render) ────────────────────

  private _updateTriggerInPlace(): void {
    const trigger = this.shadowRoot?.querySelector('[part="trigger"]') as HTMLElement | null;
    if (!trigger) return;
    trigger.innerHTML = this._renderTriggerInner();
    this._wireTriggerInnerListeners(trigger);
  }

  private _updateOptionsStateInPlace(): void {
    const sr = this.shadowRoot;
    if (!sr) return;
    const optionEls = sr.querySelectorAll('[role="option"]');
    const opts = this._effectiveOptions;
    optionEls.forEach(el => {
      const idx = parseInt(el.getAttribute('data-index') || '-1', 10);
      const opt = opts[idx];
      if (!opt) return;
      const selected = this._isOptionSelected(opt);
      el.classList.toggle('qds-option-selected', selected);
      el.setAttribute('aria-selected', String(selected));
      const check = el.querySelector('[part="option-check"]');
      if (check) {
        check.innerHTML = selected ? '<i class="pi pi-check"></i>' : '';
      }
    });
  }

  // ── Validation ──────────────────────────────────────────────────

  private _updateValidity(): void {
    if (this._customValidityMsg) {
      // customError already set via setCustomValidity
      return;
    }
    if (this._required && !this._hasValue()) {
      this._validityTracker.setValidity(
        { valueMissing: true, valid: false },
        'Please select an option.',
      );
    } else {
      this._validityTracker.setValidity(
        { valueMissing: false, valid: true },
        '',
      );
    }
  }

  private _showErrorState(): void {
    this._updateValidity();
    if (!this._validityTracker.valid) {
      this.setAttribute('data-invalid', '');
    } else {
      this.removeAttribute('data-invalid');
    }
  }

  private _getErrorText(): string {
    if (this._errorText) return this._errorText;
    if (this._hasShownError && !this._validityTracker.valid) {
      return this._validityTracker.message || 'Please correct this field.';
    }
    return '';
  }

  private _isInvalid(): boolean {
    return this._invalid || this.hasAttribute('data-invalid');
  }

  private _isErrorVisible(): boolean {
    return !!this._getErrorText();
  }

  // ── Focus ───────────────────────────────────────────────────────

  private _handleFocusOut(e: FocusEvent): void {
    const related = e.relatedTarget as Node | null;
    if (related && this.shadowRoot?.contains(related)) {
      return; // Internal focus move
    }
    if (related === null && this._listboxPointerActive) {
      this._listboxBlurPending = true;
      return;
    }
    this._handleFocusLeave();
  }

  private _handleFocusLeave(): void {
    // Focus left the component
    if (this._open) {
      this._closeListbox();
    }
    if (this._validateOn === 'blur') {
      this._hasShownError = true;
      this._showErrorState();
      this.render();
    }
  }

  // ── Slotted options ─────────────────────────────────────────────

  private _readSlottedOptions(): void {
    const opts: QdsOption[] = [];
    this.querySelectorAll(':scope > option').forEach(opt => {
      opts.push({
        value: opt.getAttribute('value') ?? opt.textContent?.trim() ?? '',
        label: opt.textContent?.trim() ?? opt.getAttribute('value') ?? '',
        disabled: opt.hasAttribute('disabled'),
        group: opt.getAttribute('data-group') ?? undefined,
      });
    });
    this._slottedOptions = opts;
  }

  // ── Helpers ─────────────────────────────────────────────────────

  private _escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private get _allOptions(): QdsOption[] {
    return this._options.length > 0 ? this._options : this._slottedOptions;
  }

  private get _effectiveOptions(): QdsOption[] {
    const opts = this._allOptions;
    if (!this._filterQuery) return opts;
    const q = this._filterQuery.toLowerCase();
    return opts.filter(o =>
      o.label.toLowerCase().includes(q) ||
      (o.description?.toLowerCase().includes(q) ?? false),
    );
  }
}

if (!customElements.get('qds-select')) {
  customElements.define('qds-select', QdsSelect);
}
